/* eslint-disable react/no-unstable-nested-components */
import React, { ReactNode, useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import {
  binderQueries,
  publishBinder,
  unpublishBinder,
} from '../../../../api/binder'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitQueries } from '../../../../api/units'
import { entranceQueries } from '../../../../api/society-entrances'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import {
  ButtonProps,
  ButtonVariant,
} from '../../../../components/common/Button'
import { Icon, IconChoices } from '../../../../components/common/Icon'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { BinderType, mapUnitToBinderType } from './helper'
import { exportBindersToCsv } from './exportBindersCsv'
import { useModal } from '../../../../hooks/useModal'
import { UploadBinderModal } from './UploadBinderModal'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { CreateUpdateBinderDocumentModal } from '../../../../components/society/Binder/CreateUpdateBinderDocumentModal'
import { sortStringsAlphabetically } from '../../../../helpers/sorting'

const columnHelper = createColumnHelper<BinderType>()

export const BinderPickerView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set())
  const [searchString, setSearchString] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const { setToastNotification } = useToastNotifications()
  const navigate = useNavigate()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:binder', {
      '{{ societyName }}': society?.name,
    })
  )
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id || '')
  )
  const { data: societyEntrences } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )
  const { data: binderData } = useQuery(
    binderQueries.getAllBinders(society?._id || '')
  )

  const filteredUnits = useMemo(() => {
    if (societyUnits) {
      return societyUnits
        .filter((unit) => {
          if (unit.isUnassigned) return false
          return true
        })
        .map((unit) => {
          const binderSummary = binderData?.find(
            (binder) => binder.unitId === unit._id
          )
          return mapUnitToBinderType(
            unit,
            society,
            societyEntrences,
            binderSummary
          )
        })
        .sort((a, b) => sortStringsAlphabetically(a.adress, b.adress))
    }
    return []
  }, [societyUnits, binderData, society, societyEntrences])

  const selectedBindersIds = useMemo(() => {
    return filteredUnits.flatMap((row) => {
      if (checkedRows.has(row.unitId)) {
        const binder = binderData?.find((b) => b.unitId === row.unitId)
        return binder ? [binder.binderId] : []
      }
      return []
    })
  }, [checkedRows, filteredUnits, binderData])

  const published = useMemo(() => {
    const selectedUnits = filteredUnits.filter((row) =>
      checkedRows.has(row.unitId)
    )
    if (selectedUnits.length === 0) return false
    const allArePublished = selectedUnits.every((unit) => unit.publishedAt)
    return allArePublished
  }, [filteredUnits, checkedRows])

  const checkboxRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate =
        checkedRows.size > 0 && checkedRows.size < filteredUnits.length
    }
  }, [checkedRows, filteredUnits.length])

  const {
    show: showCreateBinderModal,
    open: openCreateBinderModal,
    close: closeCreateBinderModal,
  } = useModal()

  const {
    show: showCreateDocumentModal,
    open: openCreateDocumentModal,
    close: closeCreateDocumentModal,
  } = useModal()

  const { mutate: mutatePublishBinder } = useMutation({
    mutationFn: () =>
      publishBinder(society?._id as string, {
        binderIds: Array.from(checkedRows),
      }),
    onSuccess: () => {
      setCheckedRows(new Set())
      setToastNotification(
        ToastType.SUCCESS,
        translate('managementBinderView.flashMessage.publishBinderSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('managementBinderView.flashMessage.publishBinderFailure')
      )
    },
  })

  const { mutate: mutateUnpublishBinder } = useMutation({
    mutationFn: () =>
      unpublishBinder(society?._id as string, {
        binderIds: Array.from(checkedRows),
      }),
    onSuccess: () => {
      setCheckedRows(new Set())
      setToastNotification(
        ToastType.SUCCESS,
        translate('managementBinderView.flashMessage.unPublishBinderSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('managementBinderView.flashMessage.unPublishBinderFailure')
      )
    },
  })

  const btnInfo = React.useMemo(() => {
    if (checkedRows.size === 0) {
      return {
        label: translate('binderPickerView.publishButton'),
        onClick: () => mutatePublishBinder(),
      }
    }
    const hasUnpublished = Array.from(checkedRows).some((row) => {
      const binder = binderData?.find((b) => b.unitId === row)
      return !binder?.publishedAt
    })
    if (hasUnpublished) {
      return {
        label: translate('binderPickerView.publishButton'),
        onClick: () => mutatePublishBinder(),
      }
    }
    return {
      label: translate('binderPickerView.unpublishButton'),
      onClick: () => mutateUnpublishBinder(),
    }
  }, [
    checkedRows,
    binderData,
    translate,
    mutatePublishBinder,
    mutateUnpublishBinder,
  ])

  const handleCheckAll = React.useCallback(
    (checked: boolean) => {
      if (checked) {
        setCheckedRows(new Set(filteredUnits.map((row) => row.unitId)))
      } else {
        setCheckedRows(new Set())
      }
    },
    [filteredUnits]
  )

  const handleCheckRow = React.useCallback(
    (unitId: string, checked: boolean) => {
      setCheckedRows((prevSet) => {
        const newSet = new Set(prevSet)
        if (checked) {
          newSet.add(unitId)
        } else {
          newSet.delete(unitId)
        }
        return newSet
      })
    },
    []
  )

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('function', {
        id: 'function',
        header: () => (
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={
              checkedRows.size === filteredUnits.length &&
              filteredUnits.length > 0
            }
            onChange={(e) => handleCheckAll(e.target.checked)}
          />
        ),
        cell: (info) => (
          <input
            type="checkbox"
            checked={checkedRows.has(info.row.original.unitId)}
            onChange={(e) =>
              handleCheckRow(info.row.original.unitId, e.target.checked)
            }
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor('adress', {
        cell: (info) => <div className="text-left">{info.getValue()}</div>,
        header: () => (
          <div className="text-left">
            {translate('binderPickerView.table.adress')}
          </div>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('aptNumber', {
        id: 'aptNumber',
        cell: (info) => info.getValue(),
        header: translate('binderPickerView.table.aptNumber'),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('floor', {
        header: translate('binderPickerView.table.floor'),
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor(
        (row) =>
          row.folderCount === 0 && row.fileCount === 0
            ? '-- / --'
            : `${row.folderCount} / ${row.fileCount}`,
        {
          id: 'folderAndDocs',
          header: translate('binderPickerView.table.folderAndDocs'),
          footer: (info) => info.column.id,
        }
      ),
      columnHelper.accessor('publishedAt', {
        header: translate('binderPickerView.table.published'),
        cell: (info) =>
          info.getValue() ? (
            <div className="flex justify-center">
              <Icon icon={IconChoices.CHECK_CIRCLE} highlighted />
            </div>
          ) : (
            ''
          ),
        footer: (info) => info.column.id,
      }),
    ],
    [checkedRows, filteredUnits, handleCheckAll, handleCheckRow, translate]
  )

  const filteredData = React.useMemo(() => {
    if (!searchString.trim()) return filteredUnits
    const lower = searchString.toLowerCase()
    return filteredUnits.filter((row) => {
      // Check address, aptNumber - add more columns later?
      return (
        (row.adress && row.adress.toLowerCase().includes(lower)) ||
        (row.aptNumber && String(row.aptNumber).toLowerCase().includes(lower))
      )
    })
  }, [filteredUnits, searchString])

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const handleRowClick = (unitId: string): void => {
    navigate(
      reverseUrl('management:binder-unit', {
        id: society?._id as string,
        unitId,
      })
    )
  }

  const handleExportCsv = async (): Promise<void> => {
    if (!society?._id || isExporting || checkedRows.size === 0) return
    setIsExporting(true)
    try {
      const checkedUnits = filteredUnits.filter((unit) =>
        checkedRows.has(unit.unitId)
      )
      await exportBindersToCsv(society._id, checkedUnits, {
        adress: translate('binderPickerView.table.adress'),
        aptNumber: translate('binderPickerView.table.aptNumber'),
        folder: translate('binderPickerView.csv.folder'),
        folderDescription: translate('binderPickerView.csv.folderDescription'),
        document: translate('binderPickerView.csv.document'),
        published: translate('binderPickerView.table.published'),
        yes: translate('common.yes'),
        no: translate('common.no'),
      })
    } catch {
      setToastNotification(
        ToastType.DANGER,
        translate('binderPickerView.flashMessage.exportCsvFailure')
      )
    } finally {
      setIsExporting(false)
    }
  }

  const getButtons = (): ButtonProps[] => {
    return [
      {
        label: btnInfo.label,
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.BOOK,
        disabled: checkedRows.size === 0,
        onClick: btnInfo.onClick,
      },
      {
        label: translate('binderPickerView.uploadButtonFolders'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.DOCUMENT_ADD,
        disabled: checkedRows.size === 0,
        onClick: openCreateBinderModal,
      },
      {
        label: translate('binderPickerView.uploadButtonDocuments'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.DOCUMENT_ADD,
        disabled: checkedRows.size === 0,
        onClick: openCreateDocumentModal,
      },
      {
        label: translate('binderPickerView.exportCsvButton'),
        variant: ButtonVariant.DEFAULT,
        icon: IconChoices.DOWNLOAD,
        loading: isExporting,
        disabled: isExporting || checkedRows.size === 0,
        onClick: handleExportCsv,
      },
    ]
  }
  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('binderPickerView.title')}
        searchBar={{ value: searchString, onChange: onChangeSearchString }}
        buttons={getButtons()}
        placeholder={translate('binderPickerView.searchBar.placeholder')}
      >
        <table className="w-full">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="p-4">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="text-center p-4">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="even:bg-gray-100 hover:cursor-pointer hover:bg-gray-200"
                onClick={() => handleRowClick(row.original.unitId)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="text-center p-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {showCreateBinderModal && (
          <UploadBinderModal
            rows={filteredUnits.filter((row) => checkedRows.has(row.unitId))}
            show={showCreateBinderModal}
            close={closeCreateBinderModal}
            societyId={society?._id as string}
            published={published}
          />
        )}
        {showCreateDocumentModal && (
          <CreateUpdateBinderDocumentModal
            show={showCreateDocumentModal}
            close={closeCreateDocumentModal}
            binderId={selectedBindersIds}
            rows={filteredUnits.filter((row) => checkedRows.has(row.unitId))}
            published={published}
          />
        )}
      </ViewBase>
    </ErrorBoundary>
  )
}
